import isEmpty from 'lodash/isEmpty'
import type { Order, Payment } from '@ztechapps/common.contract'
import { PaymentMethodEnum, PaymentStatusEnum } from '@ztechapps/common.contract'
import { roundNumber, toValidNumber } from '@ztechapps/common.utils'

const REFUNDABLE_METHODS = [PaymentMethodEnum.card, PaymentMethodEnum.achDirectDebit, PaymentMethodEnum.bankTransfer]
const REFUNDABLE_STATUSES = [
  PaymentStatusEnum.paid,
  PaymentStatusEnum.partiallyPaid,
  PaymentStatusEnum.partiallyRefunded,
]

/**
 * The single "is this source payment actually refundable through the gateway?" rule, shared by the
 * backend (ConfirmReturnOrder allocation validation, ProcessReturnRefund per-leg re-check) and the
 * webui chooser — one money rule, no drift between layers.
 *
 * Refundable = the payment went through Stripe (a paymentIntent or charge id exists to refund
 * against), was taken via a gateway method, and has captured money on it. Pending/uncaptured
 * entries have nothing captured to refund; cash/free/relatedOrder never hit the gateway.
 */
export const isRefundablePayment = (payment?: Payment): boolean => {
  const hasStripeInfo = !isEmpty(payment?.stripeInfo?.paymentIntent) || !isEmpty(payment?.stripeInfo?.paymentId)
  return (
    hasStripeInfo &&
    REFUNDABLE_METHODS.includes(payment?.method as PaymentMethodEnum) &&
    REFUNDABLE_STATUSES.includes(payment?.status as PaymentStatusEnum)
  )
}

export const getRefundablePayments = (order?: Order): Payment[] => {
  return (order?.payments ?? []).filter((payment) => isRefundablePayment(payment))
}

/**
 * How much of the payment is still refundable through the gateway: gross less the refunds already
 * recorded on it, 2dp. Allocating beyond this would confirm a leg Stripe can never satisfy.
 */
export const getRefundableRemainder = (payment?: Payment): number => {
  const refunded = (payment?.refunds ?? []).reduce((total, refund) => total + toValidNumber(refund?.amount), 0)
  return roundNumber(toValidNumber(payment?.gross) - refunded, 2)
}
